import { AuthenticationGuard } from "@authentication/components/AuthenticationGuard";
import { authenticationService } from "@authentication/services/AuthenticationService";
import { EnvironmentLabel } from "@components/EnvironmentLabel";
import {
  AcquisitionRoutes,
  FrontSiteRoutes,
  BackOfficeRoutes,
} from "@routes/urls";
import { profileApi } from "@api/profile/profile";
import PropTypes from "prop-types";
import React, { Component } from "react";
import Overridable from "react-overridable";
import { Link, withRouter } from "react-router-dom";
import {
  Container,
  Divider,
  Header,
  Icon,
  Menu,
  Image,
} from "semantic-ui-react";
import AdminMenu from "./AdminMenu";
import { clearAllFiltersFromSession } from "@utils/SessionStorage";

class Sidebar extends Component {
  constructor(props) {
    super(props);
    this.state = {
      profileImage: null,
      imageLoading: true,
      imageError: false,
    };
  }

  componentDidMount() {
    this.loadProfileImage();
  }

  componentDidUpdate(prevProps) {
    // Reload image if user changes
    if (prevProps.user?.id !== this.props.user?.id) {
      this.loadProfileImage();
    }
  }

  loadProfileImage = async () => {
    try {
      this.setState({ imageLoading: true, imageError: false });
      const imageInfo = await profileApi.getImageInfo();

      if (imageInfo.has_image && imageInfo.image_url) {
        this.setState({
          profileImage: imageInfo.image_url,
          imageLoading: false,
          imageError: false,
        });
      } else {
        this.setState({
          profileImage: null,
          imageLoading: false,
          imageError: false,
        });
      }
    } catch (error) {
      this.setState({
        profileImage: null,
        imageLoading: false,
        imageError: true,
      });
    }
  };

  logout = async () => {
    const { logout, sendErrorNotification } = this.props;
    try {
      clearAllFiltersFromSession();
      await logout();
      await logout();
      window.location.href = FrontSiteRoutes.home;
    } catch (e) {
      sendErrorNotification(
        "Logout",
        "Something went wrong. Please try to logout again."
      );
    }
  };

  removeTrailingSlashes = (path) => path.replace(/\/+$/, "");

  renderProfileIcon = () => {
    const { profileImage, imageLoading, imageError } = this.state;

    if (imageLoading) {
      return <Icon name="user circle" color="grey" loading />;
    }

    if (profileImage && !imageError) {
      return (
        <Image
          src={profileImage}
          avatar
          size="tiny"
          style={{
            width: "55px",
            height: "55px",
            objectFit: "cover",
            border: "2px solid #f4b952",
            backgroundColor: "#f8f8f8",
          }}
          onError={() => {
            this.setState({ imageError: true });
          }}
        />
      );
    }
    return <Icon name="user circle" color="grey" />;
  };

  render() {
    const { location, user } = this.props;
    const isAdmin = authenticationService.hasRoles(user, ["admin"]);
    const activePath = this.removeTrailingSlashes(location.pathname);
    const locationsActive = activePath.includes(BackOfficeRoutes.locationsList);
    const documentsActive = activePath.includes(BackOfficeRoutes.documentsList);
    const documentRequestsActive = activePath.includes(
      BackOfficeRoutes.documentRequestsList
    );
    const ordersActive = activePath.includes(AcquisitionRoutes.ordersList);
    const eitemsActive = activePath.includes(BackOfficeRoutes.eitemsList);
    const loansActive = activePath.includes(BackOfficeRoutes.loansList);
    const itemsActive = activePath.includes(BackOfficeRoutes.itemsList);
    const patronsActive = activePath.includes(BackOfficeRoutes.patronsList);
    const statsMostLoanedActive =
      activePath === BackOfficeRoutes.stats.mostLoaned;
    const statsOverdueActive = activePath === BackOfficeRoutes.stats.overdue;
    const statsTopBorrowersActive =
      activePath === BackOfficeRoutes.stats.topBorrowers;
    const statsDocumentUsage =
      activePath === BackOfficeRoutes.stats.documentUsage;
    const statsPickBorrowingActive =
      activePath === BackOfficeRoutes.stats.pickBorrowing;
    const statsOpacUsageActive =
      activePath === BackOfficeRoutes.stats.opacUsage;
    const overviewActive = activePath === BackOfficeRoutes.home;
    const checkInActive = activePath.includes(BackOfficeRoutes.checkIn);
    const checkOutActive = activePath.includes(BackOfficeRoutes.checkOut);
    const specialColActive = activePath.includes(
      BackOfficeRoutes.specialColList
    );
    const auditLogsActive = activePath.includes(BackOfficeRoutes.auditLogs);
    const attendaceSummaryActive = activePath.includes(
      BackOfficeRoutes.attendaceSummary
    );

    return (
      <Overridable id="Sidebar.layout">
        <>
          <Container textAlign="center">
            <EnvironmentLabel pointing="below" classContext="backoffice" />
          </Container>
          <Header as="h3" className="bo-menu-header">
            {this.renderProfileIcon()}
            <Header.Content>
              {user.fullName}
              <Header.Subheader className="bo-menu-subheader">
                {isAdmin ? "Admin" : "Librarian"}
              </Header.Subheader>
            </Header.Content>
          </Header>
          <Overridable id="Sidebar.Menu">
            <>
              {!isAdmin && (
                <>
                  <Menu text vertical className="bo-menu bo-menu-subheader">
                    <Menu.Item>
                      <Menu.Menu>
                        <Menu.Item
                          as={Link}
                          to={FrontSiteRoutes.home}
                          style={{ paddingLeft: "1rem" }}
                        >
                          Go to OPAC
                        </Menu.Item>
                      </Menu.Menu>
                    </Menu.Item>
                  </Menu>
                </>
              )}

              <Divider />

              <Menu text vertical className="bo-menu">
                {!isAdmin && (
                  <Menu.Item>
                    <Menu.Header
                      style={{
                        marginRight: "2px",
                        marginLeft: "-21px",
                        paddingLeft: "1rem",
                      }}
                    >
                      <Icon name="play" size="small" />
                      Actions
                    </Menu.Header>
                    <Menu.Menu>
                      <Menu.Item
                        as={Link}
                        active={checkInActive}
                        to={BackOfficeRoutes.checkIn}
                        style={{ paddingLeft: "2rem" }}
                      >
                        Check-in
                      </Menu.Item>
                      <Menu.Item
                        as={Link}
                        active={checkOutActive}
                        to={BackOfficeRoutes.checkOut}
                        style={{ paddingLeft: "2rem" }}
                      >
                        Check-out
                      </Menu.Item>
                      <Menu.Item
                        as={Link}
                        active={activePath.includes(
                          BackOfficeRoutes.documentLabels
                        )}
                        to={BackOfficeRoutes.documentLabels}
                        style={{ paddingLeft: "2rem" }}
                      >
                        Document Labels
                      </Menu.Item>
                    </Menu.Menu>
                  </Menu.Item>
                )}

                {!isAdmin && (
                  <Menu.Item>
                    <Menu.Header
                      style={{
                        marginRight: "2px",
                        marginLeft: "-21px",
                        paddingLeft: "1rem",
                      }}
                    >
                      <Icon name="building" size="small" />
                      Library
                    </Menu.Header>
                    <Menu.Menu>
                      <Menu.Item
                        as={Link}
                        active={overviewActive}
                        to={BackOfficeRoutes.home}
                        style={{ paddingLeft: "2rem" }}
                      >
                        Overview
                      </Menu.Item>
                      <Menu.Item
                        as={Link}
                        active={loansActive}
                        to={BackOfficeRoutes.loansList}
                        style={{ paddingLeft: "2rem" }}
                      >
                        Loans
                      </Menu.Item>
                      <Menu.Item
                        as={Link}
                        active={documentRequestsActive}
                        to={BackOfficeRoutes.documentRequestsList}
                        style={{ paddingLeft: "2rem" }}
                      >
                        Book Request
                      </Menu.Item>
                      <Menu.Item
                        as={Link}
                        active={locationsActive}
                        to={BackOfficeRoutes.locationsList}
                        style={{ paddingLeft: "2rem" }}
                      >
                        Locations
                      </Menu.Item>
                      <Menu.Item
                        as={Link}
                        active={activePath.includes(
                          BackOfficeRoutes.fineManagement
                        )}
                        to={BackOfficeRoutes.fineManagement}
                        style={{ paddingLeft: "2rem" }}
                      >
                        Fees
                      </Menu.Item>
                    </Menu.Menu>
                  </Menu.Item>
                )}

                {!isAdmin && (
                  <Overridable
                    activePath={activePath}
                    id="Backoffice.Sidebar.Menu.Catalogue"
                  >
                    <Menu.Item>
                      <Menu.Header
                        style={{
                          marginRight: "2px",
                          marginLeft: "-21px",
                          paddingLeft: "1rem",
                        }}
                      >
                        <Icon name="list" size="small" />
                        Catalog
                      </Menu.Header>
                      <Menu.Menu>
                        <Menu.Item
                          as={Link}
                          active={documentsActive}
                          to={BackOfficeRoutes.documentsList}
                          style={{ paddingLeft: "2rem" }}
                        >
                          Bibliographic Records
                        </Menu.Item>
                        <Menu.Item
                          as={Link}
                          active={specialColActive}
                          to={BackOfficeRoutes.specialColList}
                          style={{ paddingLeft: "2rem" }}
                        >
                          Theses and Dissertations
                        </Menu.Item>
                        <Menu.Item
                          as={Link}
                          active={itemsActive}
                          to={BackOfficeRoutes.itemsList}
                          style={{ paddingLeft: "2rem" }}
                        >
                          Physical Copies
                        </Menu.Item>
                        <Menu.Item
                          as={Link}
                          active={eitemsActive}
                          to={BackOfficeRoutes.eitemsList}
                          style={{ paddingLeft: "2rem" }}
                        >
                          E-Items
                        </Menu.Item>
                      </Menu.Menu>
                    </Menu.Item>
                  </Overridable>
                )}

                {!isAdmin && (
                  <Menu.Item>
                    <Menu.Header
                      style={{
                        marginRight: "2px",
                        marginLeft: "-21px",
                        paddingLeft: "1rem",
                      }}
                    >
                      <Icon name="shopping bag" size="small" />
                      Acquisition
                    </Menu.Header>
                    <Menu.Menu>
                      <Menu.Item
                        as={Link}
                        active={ordersActive}
                        to={AcquisitionRoutes.ordersList}
                        style={{ paddingLeft: "2rem" }}
                      >
                        New Acquisition
                      </Menu.Item>
                    </Menu.Menu>
                  </Menu.Item>
                )}

                {!isAdmin && (
                  <Menu.Item>
                    <Menu.Header
                      style={{
                        marginRight: "2px",
                        marginLeft: "-21px",
                        paddingLeft: "1rem",
                      }}
                    >
                      <Icon name="user" size="small" />
                      Patrons
                    </Menu.Header>
                    <Menu.Menu>
                      <Menu.Item
                        as={Link}
                        active={patronsActive}
                        to={BackOfficeRoutes.patronsList}
                        style={{ paddingLeft: "2rem" }}
                      >
                        Patrons
                      </Menu.Item>
                      <Menu.Item
                        as={Link}
                        active={attendaceSummaryActive}
                        to={BackOfficeRoutes.attendaceSummary}
                        style={{ paddingLeft: "2rem" }}
                      >
                        Attendance Summary
                      </Menu.Item>
                    </Menu.Menu>
                  </Menu.Item>
                )}

                {!isAdmin && (
                  <Menu.Item>
                    <Menu.Header
                      style={{
                        marginRight: "2px",
                        marginLeft: "-21px",
                        paddingLeft: "1rem",
                      }}
                    >
                      <Icon name="chart bar" size="small" />
                      Reporting
                    </Menu.Header>
                    <Menu.Menu>
                      <Menu.Item
                        as={Link}
                        active={statsOverdueActive}
                        to={BackOfficeRoutes.stats.overdue}
                        style={{ paddingLeft: "2rem" }}
                      >
                        Overdue
                      </Menu.Item>
                      <Menu.Item
                        as={Link}
                        active={statsMostLoanedActive}
                        to={BackOfficeRoutes.stats.mostLoaned}
                        style={{ paddingLeft: "2rem" }}
                      >
                        Most Loaned
                      </Menu.Item>
                      <Menu.Item
                        as={Link}
                        active={statsTopBorrowersActive}
                        to={BackOfficeRoutes.stats.topBorrowers}
                        style={{ paddingLeft: "2rem" }}
                      >
                        Top Borrowers
                      </Menu.Item>
                      <Menu.Item
                        as={Link}
                        active={statsDocumentUsage}
                        to={BackOfficeRoutes.stats.documentUsage}
                        style={{ paddingLeft: "2rem" }}
                      >
                        Document Usage
                      </Menu.Item>
                      <Menu.Item
                        as={Link}
                        active={statsPickBorrowingActive}
                        to={BackOfficeRoutes.stats.pickBorrowing}
                        style={{ paddingLeft: "2rem" }}
                      >
                        Peak Borrowing
                      </Menu.Item>
                      <Menu.Item
                        as={Link}
                        active={statsOpacUsageActive}
                        to={BackOfficeRoutes.stats.opacUsage}
                        style={{ paddingLeft: "2rem" }}
                      >
                        OPAC Usage Statistics
                      </Menu.Item>
                    </Menu.Menu>
                  </Menu.Item>
                )}

                {/* Show Library Activity Monitor for both admins and librarians */}
                <Menu.Item>
                  <Menu.Header
                    style={{
                      marginRight: "2px",
                      marginLeft: "-21px",
                      paddingLeft: "1rem",
                    }}
                  >
                    <Icon name="cog" size="small" />
                    Library Activity Monitor
                  </Menu.Header>
                  <Menu.Menu>
                    <Menu.Item
                      as={Link}
                      active={auditLogsActive}
                      to={BackOfficeRoutes.auditLogs}
                      style={{ paddingLeft: "2rem" }}
                    >
                      Audit logs
                    </Menu.Item>
                  </Menu.Menu>
                </Menu.Item>
                <Overridable
                  activePath={activePath}
                  id="Backoffice.Sidebar.CustomMenuItem"
                />
              </Menu>
            </>
          </Overridable>
          <AuthenticationGuard
            silent
            authorizedComponent={() => <AdminMenu />}
            roles={["admin", "librarian"]}
            loginComponent={() => null}
          />
        </>
      </Overridable>
    );
  }
}

Sidebar.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }),
  user: PropTypes.object.isRequired,
  logout: PropTypes.func.isRequired,
  sendErrorNotification: PropTypes.func.isRequired,
};

Sidebar.defaultProps = {
  location: {
    pathname: "",
  },
};

export default Overridable.component("Sidebar", withRouter(Sidebar));
